/* global ga */

let trackerCount = 0;

function gaNotLoaded() {
    return typeof ga === 'undefined'
}

// Stolen from: analytics-plugin-google-analytics/src/browser.js
// Modified to take trackerName
function pageView(urlPath, trackerName, pageTitle) {
    if (gaNotLoaded()) return;
    const path = urlPath || document.location.pathname;
    ga(`${trackerName}.set`, 'page', path);
    ga(`${trackerName}.set`, 'title', pageTitle)
    ga(`${trackerName}.send`, 'pageview');
}

/**
 * Identify a visitor by Id
 * @param  {string} id - unique visitor ID
 * @param  {string} trackerName - The name of the tracker for Google Analytics. Needed when using multiple trackers.
 */
export function identifyVisitor(id, trackerName) {
    if (gaNotLoaded()) return;
    if (id) ga(`${trackerName}.set`, 'userId', id)
}

/**
 * Send event tracking to Google Analytics
 *
 * Stolen from: analytics-plugin-google-analytics/src/browser.js
 * Modified to take trackerName
 *
 * @param  {object} eventData - GA event payload
 * @param  {string} [eventData.hitType = 'event'] - hitType https://bit.ly/2Jab9L1 one of 'pageview', 'screenview', 'event', 'transaction', 'item', 'social', 'exception', 'timing'
 * @param  {string} [eventData.event] - event Action https://bit.ly/2CUzeoz
 * @param  {string} [eventData.label] - event Label http://bit.ly/2oo8eb3
 * @param  {string} [eventData.category] - event Category http://bit.ly/2EAy9UP
 * @param  {string} [eventData.nonInteraction = false] - nonInteraction https://bit.ly/2CUzeoz
 * @param  {string} trackerName - The name of the tracker for Google Analytics. Needed when using multiple trackers.
 * @return {object} sent data
 */
function trackEvent(eventData, trackerName) {
    if (gaNotLoaded()) return;

    const data = {
        // hitType https://bit.ly/2Jab9L1 one of 'pageview', 'screenview', 'event', 'transaction', 'item', 'social', 'exception', 'timing'
        hitType: eventData.hitType || 'event',
        // eventAction https://bit.ly/2CUzeoz
        eventAction: eventData.event,
        // eventLabel http://bit.ly/2oo8eb3
        eventLabel: eventData.label,
        // eventCategory http://bit.ly/2EAy9UP
        eventCategory: eventData.category || 'All',
        // nonInteraction https://bit.ly/2CUzeoz
        nonInteraction: (eventData.nonInteraction !== undefined) ? !!eventData.nonInteraction : false
    };

    if (eventData.value !== undefined) {
        data.eventValue = format(eventData.value)
    }

    /* Attach campaign data */
    const campaignData = eventData.campaign || {};
    const {name, source, medium, content, keyword} = campaignData;
    if (name) data.campaignName = name;
    if (source) data.campaignSource = source;
    if (medium) data.campaignMedium = medium;
    if (content) data.campaignContent = content;
    if (keyword) data.campaignKeyword = keyword;

    /* Send data to Google Analytics */
    ga(`${trackerName}.send`, 'event', data);
    return data
}


function format(value) {
    if (!value || value < 0) return 0;
    return Math.round(value)
}

/**
 * Set any custom dimensions found in the data.
 *
 * @param config - The analytics config object.
 * @param data - The event payload containing the custom dimensions to be set.
 */
function setCustomDimensions(config, data) {
    if (config.dimensions) {
        // Convert the payload key to its dimension index
        // e.g. 'rate_card': 'Staff' -> 'dimension1': 'Staff'
        for (let [key, value] of Object.entries(data)) {
            if (key in config.dimensions) {
                let dimension_index = config.dimensions[key];
                // Convert array values to a comma separated string
                if (Array.isArray(value)) value = value.join(',');
                ga(`${config.trackerName}.set`, dimension_index, value);
            }
        }
    }
}

/**
 * Custom implementation of the Google Analytics plugin for the Analytics library.
 *
 * Re-implements all of the provided Google Analytics functionality so that we can support multiple trackers.
 *
 * For custom dimensions to work the plugin must pass be passed a 'dimensions' key in the config. This
 * is a mapping of keys to their appropriate dimension index.
 *
 * When multiple plugins are defined the first plugin will use tracker name `t0` and each subsequent plugin will
 * use an incremented tracker name. So a second plugin instance would use a name of `t1`.
 *
 * For this call to map correctly in Google analytics:
 * ```js
 * analytics.identify('userId12', { 'user_status': Anonymous });
 * ```
 *
 * This plugin would need to be configured like:
 * ```js
 * anvilGoogleAnalytics({
 *     trackingId: 'someID',
 *     dimensions: {
 *         'user_status': 'dimension1',
 *     }
 * });
 * ```
 *
 * Any traits passed to `identify()` will be ignored if they are not defined in the `dimensions` config.
 *
 * @param pluginConfig - dictionary containing the information needed to create the FB pixel.
 * @param pluginConfig.trackingId: The Google analytics tracking ID.
 * @param pluginConfig.dimensions: A mapping of page/user fields to their corresponding google analytics dimension/metric name.
 * @param pluginConfig.pageMeta: Information about the page events are being fired on.
 */
export default function anvilGoogleAnalytics(pluginConfig) {
    pluginConfig.trackerName = `t${trackerCount}`;
    trackerCount += 1;

    return {
        // Name needs to be different so that the analytics initialization doesn't complain about duplicate plugins
        name: `google-analytics-${pluginConfig.trackerName}`,
        config: pluginConfig,
        initialize: ({config}) => {
            if (!config.trackingId) throw new Error('No google analytics trackingId defined');
            if (gaNotLoaded()) {
                /* eslint-disable */
                (function (i, s, o, g, r, a, m) {
                    i['GoogleAnalyticsObject'] = r;
                    i[r] = i[r] || function () {
                        (i[r].q = i[r].q || []).push(arguments)
                    }, i[r].l = 1 * new Date();
                    a = s.createElement(o),
                        m = s.getElementsByTagName(o)[0];
                    a.async = 1;
                    a.src = g;
                    m.parentNode.insertBefore(a, m)
                })(window, document, 'script', 'https://www.google-analytics.com/analytics.js', 'ga')
                /* eslint-enable */
            }
            ga('create', config.trackingId, 'auto', config.trackerName);
            ga(`${config.trackerName}.require`, 'ec');

            if (config.debug) {
                // Disable sends to GA http://bit.ly/2Ro0vTR
                ga(`${config.trackerName}.set`, 'sendHitTask', null);
                window.ga_debug = {trace: true}
            }
        },
        page: ({payload, config}) => {
            const {properties} = payload;
            const allPageData = JSON.parse(JSON.stringify(properties));

            // Set any custom dimensions found in the data before sending the page view
            setCustomDimensions(config, allPageData);

            // Stolen from: analytics-plugin-google-analytics/src/browser.js
            pageView(properties.path, config.trackerName, properties.page_title);
        },
        identify: ({payload, config}) => {
            // Only set the userId if one was provided and it wasn't for an anonymous user
            // This is so that we can take advantage of Google Analytics session unification.
            // https://support.google.com/analytics/answer/3123666#SetUpUserID
            if (payload.userId && !payload.userId.startsWith('anon-')) {
                identifyVisitor(payload.userId, config.trackerName);
            }

            setCustomDimensions(config, payload.traits);
        },
        track: ({payload, config, instance}) => {
            // Stolen from: analytics-plugin-google-analytics/src/browser.js
            // Modified to use trackerName and to standardize events
            const {properties} = payload;
            let {event} = payload;
            let {label, value, category, nonInteraction, products, product} = properties;
            const campaign = instance.getState('context.campaign');
            let action = event;

            if (products) {
                for (let product of products) {
                    let couponNames = [];
                    if (product.trial) {
                        couponNames.push('trial');
                    }
                    if (product.discount) {
                        couponNames.push('discount');
                    }
                    const productDetails = {
                        id: product.id,
                        name: product.name,
                        category: `${product.type}/${product.category}`,
                        price: parseFloat(product.price),
                        quantity: parseInt(product.quantity),
                        position: product.position,
                        coupon: couponNames.join()
                    };
                    ga(`${config.trackerName}.ec:addProduct`, productDetails);
                }
            }

            /**
             * Map our events to the correct google analytics events.
             *
             * For events that are a part of the order tunnel will also send the correct ecommerce information.
             *
             * See `analytics/README.md` for details on the events.
             */
            switch (event) {
                case 'AddPaymentInfo':
                    category = 'Order Tunnel';
                    action = "add_payment_info";
                    if (products) {
                        label = `${properties.store}: ${properties.payment_method}`;
                    }
                    value = properties.total;
                    ga(`${config.trackerName}.ec:setAction`, 'checkout', {
                        'step': properties.step,
                        'option': properties.payment_method
                    });
                    break;
                case 'AddToCart':
                    category = 'Order Tunnel';
                    action = "add_to_cart";
                    if (product) {
                        label = product.name;
                        value = product.price;
                    }
                    ga(`${config.trackerName}.ec:setAction`, 'add', {
                        'step': properties.step,
                    });
                    break;
                case 'CompleteLogin':
                    if (properties.login_method.toUpperCase() === 'DIRECT') {
                        category = 'Auth';
                    } else {
                        // Social log in
                        category = properties.login_method;
                        ga(`${config.trackerName}.send`, 'social', properties.login_method, 'login', properties.page);
                    }
                    action = 'login';
                    label = properties.page;
                    break;
                case 'CompleteLogout':
                    category = 'Auth';
                    action = 'logout';
                    break;
                case 'CompleteRegister':
                    if (properties.login_method.toUpperCase() === 'DIRECT') {
                        category = 'Auth';
                    } else {
                        // Social registration
                        category = properties.login_method;
                        ga(`${config.trackerName}.send`, 'social', properties.login_method, 'register', properties.page);
                    }
                    action = 'sign_up';
                    label = properties.page;
                    break;
                case 'CompleteRegistrationWall':
                    category = 'Order Tunnel';
                    action = 'complete_registration_wall';
                    label = `${properties.method}: ${properties.page}`;
                    nonInteraction = true;
                    break;
                case 'DisplayReadPrompt':
                    category = 'Order Tunnel';
                    action = 'display_read_prompt';
                    label = properties['page'];
                    value = properties['views_remaining'];
                    nonInteraction = true;
                    break;
                case 'DisplayRegistrationWall':
                    category = 'Order Tunnel';
                    action = 'display_registration_wall';
                    label = properties['page'];
                    nonInteraction = true;
                    break;
                case 'DisplaySubscribePrompt':
                    category = 'Order Tunnel';
                    action = 'display_subscribe_prompt';
                    label = properties['page'];
                    value = properties['views_remaining'];
                    nonInteraction = true;
                    break;
                case 'FormSubmitInvalid': {
                    action = "form_submit_invalid";
                    const error_dict = properties.errors;
                    let error_messages_string = "";
                    let error_count = 0;
                    if (error_dict) {
                        // Turn the dictionary of errors into a comma separated string of the error messages grouped by the field
                        // name where different field error messages are separated by a semicolon. This is then used for the label.
                        // Also total the number of errors to use as the value for the event.
                        for (let [field_name, error_list] of Object.entries(error_dict)) {
                            // Separate different field messages with a semicolon.
                            if (error_messages_string !== "") {
                                error_messages_string += ";"
                            }
                            error_messages_string += `${field_name}:`;
                            error_messages_string += error_list.join();
                            error_count += error_list.length;
                        }
                    }
                    if (error_messages_string) {
                        label = error_messages_string;
                    }
                    value = error_count;
                    break;
                }
                case 'LoadedContentView':
                    category = 'Infinite Scroll';
                    label = properties.page;
                    value = properties.loaded_content_number;
                    nonInteraction = true;
                    break;
                case 'MailchimpSignup': {
                    category = 'Mailchimp';
                    action = 'newsletter_sign_up';
                    let mcEmailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (mcEmailSignupNames !== '') {
                                mcEmailSignupNames += ',';
                            }
                            mcEmailSignupNames += signupName;
                        }
                    }
                    label = mcEmailSignupNames;
                    break;
                }
                case 'PrintSubscriberRequest':
                    if (!Object.prototype.hasOwnProperty.call(properties, 'is_print_subscriber')) {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    if (properties.is_print_subscriber !== 'on') {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    category = 'Auth';
                    action = 'print_subscriber_request';
                    break;
                case 'Purchase':
                    category = 'Order Tunnel';
                    action = "purchase";
                    label = properties.payment_method;
                    value = properties.total;
                    ga(`${config.trackerName}.ec:setAction`, 'purchase', {
                        'id': properties.order_id,
                        'revenue': parseFloat(properties.total),
                        'affiliation': properties.store,
                        'option': properties.payment_method,
                        'step': properties.step
                    });
                    break;
                case 'RevealArticle':
                    category = 'Order Tunnel';
                    action = 'reveal_article';
                    label = properties.page;
                    value = properties.views_remaining;
                    break;
                case 'Search':
                    category = 'Search';
                    action = "search";
                    label = properties.query;
                    value = properties.results_count;
                    break;
                case 'SecondStreetSignup': {
                    category = 'SecondStreet';
                    action = 'newsletter_sign_up';
                    let ssEmailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (ssEmailSignupNames !== '') {
                                ssEmailSignupNames += ',';
                            }
                            ssEmailSignupNames += signupName
                        }
                    }
                    label = ssEmailSignupNames;
                    break;
                }
                case 'Share':
                    category = properties.method;
                    if (category.toUpperCase() !== 'LINK') {
                        // Social share
                        ga(`${config.trackerName}.send`, 'social', properties.method, 'share', properties.url);
                    }
                    action = 'share';
                    label = properties.url;
                    break;
                case 'ShowOrderTunnelRegistration':
                    category = 'Order Tunnel';
                    action = "show_order_tunnel_registration";
                    break;
                case 'SubmitRegistrationWall':
                    category = 'Order Tunnel';
                    action = 'submit_registration_wall';
                    label = `${properties.method}: ${properties.page}`;
                    break;
                case 'ViewProducts':
                    category = 'Order Tunnel';
                    action = 'view_products';
                    if (products) {
                        // Generate a comma separated string of product names
                        label = products.flatMap(product => product.name).join();
                    }
                    ga(`${config.trackerName}.ec:setAction`, 'detail', {
                        'step': properties.step,
                    });
                    break;
                case 'ZipCheck':
                    category = 'Order Tunnel';
                    action = 'zip_check';
                    label = `${properties.in_area}: ${properties.zip_code}`;
                    break;
                case  'AdImpression':
                    category = 'Banner Ad';
                    action = 'impression';
                    label = `${properties.ad_unit_path}`;
                    nonInteraction = true;
                    break;
                default:
                    break;
            }

            trackEvent({
                hitType: 'event',
                event: action,
                label: label,
                category: category || 'All',
                value: value,
                nonInteraction,
                campaign: campaign
            }, config.trackerName);
        },
        loaded: () => {
            return !!window.gaplugins;
        }
    };
}
